import { filterDOMProps } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import { type ForwardedRef, forwardRef, type ReactNode } from 'react';
import { type SlotProps } from 'react-aria-components';

import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';

export type CenterBaselineProps = {
  /** The child element to center. */
  children: ReactNode;
} & SlotProps &
  DOMProps &
  UnsafeStyleProps;

/**
 * Center an element with the baseline of a sibling text element. Used in cases
 * where the related text content may wrap e.g. checkbox + label text.
 */
export const CenterBaseline = forwardRef(function CenterBaseline(
  props: CenterBaselineProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { children, className, slot, style, ...otherProps } = props;

  return (
    <div
      {...filterDOMProps(otherProps)}
      ref={ref}
      className={cn(
        // center the child element vertically
        'flex items-center',
        // align the baseline of the child element with the baseline of sibling
        // text content
        'before:invisible before:content-["⁠"]',
        // hide the element when empty. this can happen when the child element
        // is an `*Icon` component with `isHidden` set via context
        'empty:hidden',
        className
      )}
      style={style}
      slot={slot || undefined} // TS fix for nullable slot prop
    >
      {children}
    </div>
  );
});

export function centerBaseline(
  props: Omit<CenterBaselineProps, 'children'> = {}
): (icon: ReactNode) => ReactNode {
  return (icon: ReactNode) => (
    <CenterBaseline {...props}>{icon}</CenterBaseline>
  );
}
